import { apiDownloadUrl, apiGet } from './api';

export type UserSummaryRiskFlag = {
  code: string;
  severity: 'error' | 'warning' | 'info';
  label: string;
};

export type UserSummary = {
  identity: {
    userId: string;
    email: string;
    nickname: string | null;
    firstName: string | null;
    lastName: string | null;
    displayName: string;
    role: string;
    isTeacher?: boolean;
    status: string;
    city: string | null;
    postalCode: string | null;
    avatarUrl: string;
    registeredAt: string;
  };
  engagement: {
    lastLoginAt: string | null;
    lastActivityAt: string | null;
    daysSinceActivity: number | null;
    activeDaysLast30: number;
    totalSessions: number;
    averageSessionDurationMs: number;
    totalTimeMs: number;
    courseSessionsCount: number;
    lastCourseSessionAt: string | null;
  };
  subscription: {
    state: string;
    status: string | null;
    isTrial: boolean;
    planName: string | null;
    trialEndDate: string | null;
    endDate: string | null;
    totalPaid: number;
    currency: string;
    failedPaymentCount: number;
  };
  progression: {
    currentLevel: number;
    totalExperience: number;
    totalDocuments: number;
    completedDocuments: number;
    inProgressDocuments: number;
    totalBadges: number;
  };
  teacher: {
    assigned: boolean;
    teacherId: string | null;
    teacherName: string | null;
    studentCount: number | null;
    isTeaching?: boolean;
  };
  risk: {
    level: 'none' | 'medium' | 'high';
    flags: UserSummaryRiskFlag[];
  };
  refreshedAt: string;
};

export async function getUserSummary(userId: string) {
  return apiGet<{ success: boolean; data: UserSummary }>(
    `/api/v1/admin/users/${userId}/summary`
  );
}

export type StudentMusicStyleDistribution = {
  tag: string;
  label: string;
  count: number;
};

export type StudentMusicStylesAnalytics = {
  totalStudents: number;
  studentsWithStyles: number;
  studentsWithoutStyles: number;
  distribution: StudentMusicStyleDistribution[];
};

export type UsersAnalytics = {
  byRole: Array<{ role: string; count: number }>;
  byStatus: Array<{ status: string; count: number }>;
  registrationTrend: Array<{ date: string; count: number }>;
  activeLast30d: number;
  inactiveLast30d: number;
  studentsWithoutTeacher: number;
  studentsWithoutSubscription: number;
  studentsWithoutProgression?: number;
  usersWithoutActivity?: number;
  usersWithoutAvatar?: number;
  topCities: Array<{ city: string; count: number }>;
  studentMusicStyles?: StudentMusicStylesAnalytics;
};

export async function getUsersAnalytics() {
  return apiGet<{ success: boolean; data: UsersAnalytics }>('/api/v1/admin/users/analytics');
}

export function getUsersExportUrl(): string {
  return apiDownloadUrl('/api/v1/admin/users/export.csv');
}

export type FinanceSummary = {
  periodDays: number;
  kpis: {
    mrr: number;
    mrrCurrency: string;
    paidSubscribers: number;
    netRevenueInPeriod: number;
    grossRevenueInPeriod: number;
    currency: string;
    transactionCount: number;
    trialUsers: number;
    graceUsers: number;
    churnedUsers: number;
    failedPayments: number;
    trialToPaidConversionPct: number;
    averageRevenuePerPaidUser: number;
    revenueChangePct: number;
  };
  recentTransactions: Array<{
    id: string;
    userId: string;
    email?: string;
    nickname?: string;
    amount: number;
    currency: string;
    planName?: string;
    transactionDate: string;
    gateway?: string;
  }>;
  failedPayments: Array<{
    id: string;
    userId: string;
    email?: string;
    nickname?: string;
    amount: number;
    currency: string;
    transactionDate: string;
  }>;
  activeTrials: Array<{
    subscriptionId: string;
    userId: string;
    email?: string;
    nickname?: string;
    role?: string;
    planName?: string;
    planPrice?: number;
    currency?: string;
    trialStartedAt?: string;
    trialEndDate?: string;
    trialDays?: number;
    daysRemaining?: number | null;
    expiringSoon?: boolean;
  }>;
  /** Subset of activeTrials ending within 7 days (dashboard alerts) */
  trialsExpiringSoon: Array<{
    subscriptionId: string;
    userId: string;
    email?: string;
    nickname?: string;
    planName?: string;
    trialEndDate?: string;
    daysRemaining?: number;
  }>;
  planPerformance?: Array<{
    planId: string;
    planName: string;
    price?: number;
    currency: string;
    subscribers: number;
    paid: number;
    trials: number;
  }>;
};

export async function getFinanceSummary(days = 30) {
  return apiGet<{ success: boolean; data: FinanceSummary }>(
    `/api/v1/admin/finance/summary?days=${days}`
  );
}

export function getFinanceExportUrl(days = 30): string {
  return apiDownloadUrl(`/api/v1/admin/finance/export.csv?days=${days}`);
}

export type BehaviorPeriod =
  | '7d'
  | '30d'
  | '90d'
  | '6m'
  | 'this_year'
  | 'last_year'
  | 'all';

export type BehaviorSummary = {
  period: BehaviorPeriod;
  periodLabel: string;
  periodDays: number | null;
  kpis: {
    totalSessions: number;
    uniqueActiveUsers: number;
    activeUsersByLastActivity: number;
    totalTimeMs: number;
    averageSessionDurationMs: number;
    orphanedAnonymousSessions: number;
  };
  topDocuments: Array<{
    documentCode: string;
    views: number;
    uniqueUsers: number;
    totalTimeMinutes: number;
  }>;
  componentUsage: Array<{
    componentId: string;
    componentType: string;
    componentTypeLabel: string;
    componentLabel: string;
    documentId?: string | null;
    documentCode?: string;
    documentTitle?: string;
    documentLabel?: string;
    views: number;
    totalTimeMinutes: number;
  }>;
  routeViews: Array<{ route: string; views: number }>;
  featureUsage?: Array<{ feature: string; count: number; totalDurationMs: number }>;
};

export async function getBehaviorSummary(period: BehaviorPeriod = '30d') {
  return apiGet<{ success: boolean; data: BehaviorSummary }>(
    `/api/v1/admin/behavior/summary?period=${encodeURIComponent(period)}`
  );
}

export function getBehaviorExportUrl(period: BehaviorPeriod = '30d'): string {
  return apiDownloadUrl(`/api/v1/admin/behavior/export.csv?period=${encodeURIComponent(period)}`);
}

export type TeachersSummary = {
  workloadThreshold: number;
  kpis: {
    totalTeachers: number;
    totalAssignedStudents: number;
    averageStudentsPerTeacher: number;
    maxStudentsPerTeacher: number;
    teachersWithZeroStudents: number;
    overloadedTeachers: number;
    studentsWithoutTeacher: number;
  };
  teachers: Array<{
    teacherId: string;
    email?: string;
    nickname?: string;
    city?: string | null;
    postalCode?: string | null;
    studentCount: number;
    activeStudents30d: number;
    lastActivityAt?: string | null;
    sessionsThisMonth: number;
    atRiskStudents: number;
    overloaded: boolean;
  }>;
  studentsByLocation: Array<{ city: string; postalCode: string; count: number }>;
  mapPoints?: TeachersMapPoint[];
};

export type TeachersMapPoint = {
  type: 'teacher' | 'student_cluster';
  lat: number;
  lng: number;
  label: string;
  userId?: string;
  city?: string | null;
  postalCode?: string;
  count: number;
};

export type TeacherDetail = {
  workloadThreshold: number;
  teacher: {
    teacherId: string;
    email?: string;
    nickname?: string;
    firstName?: string | null;
    lastName?: string | null;
    city?: string | null;
    postalCode?: string | null;
    lastActivityAt?: string | null;
    coordinates?: { lat: number; lng: number; department?: string } | null;
  };
  stats: {
    studentCount: number;
    activeStudents30d: number;
    atRiskStudents: number;
    sessionsTotal: number;
    sessionsThisMonth: number;
    overloaded: boolean;
  };
  workloadTrend: Array<{ month: string; sessions: number; assignedStudents: number }>;
  students: Array<{
    studentId: string;
    email?: string;
    nickname?: string;
    displayName: string;
    city?: string | null;
    postalCode?: string | null;
    lastActivityAt?: string | null;
    subscriptionStatus?: string | null;
    isTrial?: boolean;
    active30d: boolean;
  }>;
};

export async function getTeachersSummary() {
  return apiGet<{ success: boolean; data: TeachersSummary }>('/api/v1/admin/teachers/summary');
}

export async function getTeacherDetail(teacherId: string) {
  return apiGet<{ success: boolean; data: TeacherDetail }>(
    `/api/v1/admin/teachers/${teacherId}/detail`
  );
}

export type TeacherSuggestion = {
  teacherId: string;
  email?: string;
  nickname?: string;
  city?: string;
  postalCode?: string;
  studentCount: number;
  score: number;
  reasons: string[];
};

export async function getTeacherSuggestions(studentId: string) {
  return apiGet<{
    success: boolean;
    data: {
      studentId: string;
      studentCity: string | null;
      studentPostalCode: string | null;
      suggestions: TeacherSuggestion[];
    };
  }>(`/api/v1/admin/teachers/suggestions/${studentId}`);
}

export type AuditLogEntry = {
  id: string;
  adminUserId: string;
  adminEmail: string;
  action: string;
  targetUserId?: string;
  targetLabel?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
};

export async function getAuditLogs(params: { userId?: string; page?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.userId) qs.set('userId', params.userId);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  return apiGet<{
    success: boolean;
    data: { logs: AuditLogEntry[]; pagination: { page: number; limit: number; total: number; pages: number } };
  }>(`/api/v1/admin/audit?${qs.toString()}`);
}

export async function exportUserDataJson(userId: string) {
  return apiGet<{ success: boolean; data: unknown }>(`/api/v1/admin/users/${userId}/export.json`);
}

export async function downloadCsv(url: string, filename: string) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function formatMoney(amount: number, currency = 'EUR') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function formatDurationMs(ms: number): string {
  if (!ms || ms < 1000) return '< 1 min';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
}
